import React, { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { getSameWeekNextYear, getSameWeekPreviousYear, getWeekTooltipLabel } from "utils/dateUtil"

const GMPBarLineChart = ({
  data,
  previousData,
  closeOut,
  closeOutPreviousYear,
}) => {
  // ── Union of all ISO weeks from both years ──────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year data ───────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!allWeeks.length)
      return { categories: [], actualDates: {}, scores: [], closeOut: [] }

    const scoreMap = {}
    const dateMap = {}
    const closeOutMap = new Map(
      (closeOut || []).map(i => [moment(i.date).isoWeek(), Number(i.closeOut)])
    )

    ;(data || []).forEach(i => {
      const w = moment(i.date).isoWeek()
      scoreMap[w] = Number(i.score) || 0
      dateMap[w] = moment(i.date).format("DD-MM-YYYY")
    })

    return {
      categories: allWeeks.map(w => `${w}`),
      actualDates: dateMap,
      scores: allWeeks.map(w =>
        scoreMap[w] !== undefined ? scoreMap[w] : null
      ),
      closeOut: allWeeks.map(w =>
        closeOutMap.has(w) ? closeOutMap.get(w) : null
      ),
    }
  }, [allWeeks, data, closeOut])

  // ── Previous year data ──────────────────────────────────────────────────
  const prevChartData = useMemo(() => {
    if (!allWeeks.length) return { actualDates: {}, scores: [], closeOut: [] }

    const scoreMap = {}
    const dateMap = {}
    const closeOutMap = new Map(
      (closeOutPreviousYear || []).map(i => [
        moment(i.date).isoWeek(),
        Number(i.closeOut),
      ])
    )

    ;(previousData || []).forEach(i => {
      const w = moment(i.date).isoWeek()
      scoreMap[w] = Number(i.score) || 0
      dateMap[w] = moment(i.date).format("DD-MM-YYYY")
    })

    return {
      actualDates: dateMap,
      scores: allWeeks.map(w =>
        scoreMap[w] !== undefined ? scoreMap[w] : null
      ),
      closeOut: allWeeks.map(w =>
        closeOutMap.has(w) ? closeOutMap.get(w) : null
      ),
    }
  }, [allWeeks, previousData, closeOutPreviousYear])

  // ── Year labels ─────────────────────────────────────────────────────────
  const currentYear = data?.length
    ? moment(data[0].date).year()
    : moment().year()

  const previousYear = previousData?.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  // ── Stats (ignore nulls) ────────────────────────────────────────────────
  const validScores = chartData?.scores?.filter(s => s !== null) || []
  const avg = validScores.length
    ? (validScores.reduce((a, b) => a + b, 0) / validScores.length).toFixed(1)
    : 0
  const max = validScores.length ? Math.max(...validScores) : 0
  const min = validScores.length ? Math.min(...validScores) : 0

  // ── Chart config ────────────────────────────────────────────────────────
  const options = {
    chart: {
      type: "bar",
      height: 320,
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        columnWidth: allWeeks.length > 60 ? "90%" : "65%",
        borderRadius: 4,
        grouped: true,
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: [0, 0, 2.5],
      curve: "smooth",
    },
    markers: {
      size: [0, 0, 3],
      strokeWidth: 0,
    },
    colors: ["#22c55e", "#f97316", "#6366f1"],
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = allWeeks[dataPointIndex]

          const currentDate = chartData.actualDates[weekNum]
          const prevDate = prevChartData.actualDates[weekNum]

          // Derive missing dates using ISO week logic
          const resolvedCurrentDate =
            currentDate || (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate =
            prevDate ||
            (currentDate ? getSameWeekPreviousYear(currentDate) : null)

          const currentLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedCurrentDate
          )
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedPrevDate
          )

          return `Current Year : ${
            currentLabel || "N/A"
          }<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined ? `${val}%` : "N/A",
      },
    },
    xaxis: {
      categories: chartData?.categories,
      overwriteCategories: chartData.categories.map((val, index) => {
        return `${index + 1}`
      }),
      labels: {
        rotate: 0,
        style: {
          colors: "#1a1a2e",
          fontSize: allWeeks.length > 60 ? "9px" : "11px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: "GMP Score (%)",
        style: {
          color: "#1a1a2e",
          fontSize: "12px",
          fontWeight: 700,
        },
      },
      labels: {
        style: {
          colors: "#1a1a2e",
          fontSize: "11px",
          fontWeight: 600,
        },
        formatter: val => `${val}%`,
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    legend: { show: false },
  }

  const series = [
    {
      name: `GMP Score (${currentYear})`,
      type: "bar",
      data: chartData?.scores,
    },
    {
      name: `Close Out Score (${currentYear})`,
      type: "bar",
      data: chartData?.closeOut,
    },
    {
      name: `GMP Score (${previousYear})`,
      type: "line",
      data: prevChartData.scores,
    },
  ]

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5
          className="mb-0 fw-bold"
          style={{ color: "#1a1a2e", fontSize: "16px" }}
        >
          GMP — Good Manufacturing Practice
        </h5>

        {/* ── Stats ── */}
        <div className="d-flex gap-3">
          {[
            {
              label: "PEAK",
              value: `${max}%`,
              color: "#22c55e",
              bg: "#f0fdf4",
            },
            {
              label: "AVERAGE",
              value: `${avg}%`,
              color: "#6366f1",
              bg: "#eef0fb",
            },
            {
              label: "LOWEST",
              value: `${min}%`,
              color: "#ef4444",
              bg: "#fef2f2",
            },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                background: stat.bg,
                borderRadius: "10px",
                padding: "10px 14px",
                borderLeft: `4px solid ${stat.color}`,
                minWidth: "90px",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600 }}>
                {stat.label}
              </div>
              <div
                style={{ fontSize: "18px", fontWeight: 800, color: stat.color }}
              >
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Chart ── */}
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={300}
        width="100%"
      />

      {/* ── Legend ── */}
      <div
        className="d-flex justify-content-center gap-4 mt-2 flex-wrap"
        style={{ borderTop: "1px solid #f0f0f0", paddingTop: "12px" }}
      >
        {[
          { color: "#22c55e", label: `GMP Score (${currentYear})`, line: false },
          { color: "#f97316", label: `Close Out Score (${currentYear})`, line: false },
          { color: "#6366f1", label: `GMP Score (${previousYear})`, line: true },
        ].map((l, i) => (
          <div key={i} className="d-flex align-items-center gap-2">
            {l.line ? (
              <span style={{ display: "inline-flex", alignItems: "center", width: 20 }}>
                <span style={{ flex: 1, height: 2.5, background: l.color, borderRadius: 2 }} />
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: l.color, flexShrink: 0 }} />
                <span style={{ flex: 1, height: 2.5, background: l.color, borderRadius: 2 }} />
              </span>
            ) : (
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: l.color,
                  display: "inline-block",
                }}
              />
            )}
            <small
              style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}
            >
              {l.label}
            </small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default GMPBarLineChart
